// "API keys" on Your account (#807): the workspace's keys, listed, made, revoked and
// rotated over `/auth/api-keys` (`api/account.ts`).
//
// **A key is shown once.** The create and the rotate answer it; nothing reads it back.
// It is held in this panel's state only until the person says they have copied it,
// and then dropped, so it is not left in the page. It is only ever text in a `<code>`,
// never an attribute: a title or a label is read aloud and shown on hover.
//
// Revoke and rotate each end the old key at once, so each asks before it is sent.
//
// **Only a role holding `api_keys:manage` is offered a write** (`auth/api_keys.py`
// `_require`): anyone else in the workspace sees the list and nothing they would be
// refused. A rotate is a revoke then a create on the server, so a failed one is read
// back: if the old key is gone, the page says so rather than that nothing changed.
//
// Each act removes the button that was pressed, so focus is moved on purpose (#809):
// to the new key while it is shown, to what asks before a revoke or rotate, and back
// to the panel's heading after.

import { useCallback, useEffect, useRef, useState, type FormEvent, type RefObject } from "react";

import type { ApiKeyActions, ApiKeyRow, MintedApiKey, ScopePreset } from "../api/account";
import { messageOf } from "../api/http";
import { Chip, Field, Notice } from "./chrome";
import { useDropOnPageHide } from "./pageHide";
import { useReadOnly } from "./readOnly";

const day = (stamp: string): string => stamp.slice(0, 10);

const ACCESS: Record<string, string> = {
  read_only: "Read only",
  full_access: "Full access",
  custom: "Chosen permissions",
};

const PRESETS: ReadonlyArray<[ScopePreset, string, string]> = [
  ["read_only", "Read only", "It can read, and change nothing."],
  ["full_access", "Full access", "It can do what your role can, except manage keys."],
];

/** The key just made, with what made it, until the person dismisses it. */
type Shown = { key: string; name: string; rotated: boolean };

function Create({
  actions,
  onMade,
  onCancel,
}: {
  actions: ApiKeyActions;
  onMade: (made: MintedApiKey) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [scope, setScope] = useState<ScopePreset>("read_only");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setProblem(null);
    setBusy(true);
    try {
      await onMade(await actions.create(name.trim(), scope));
    } catch (failure) {
      setProblem(`${messageOf(failure)} No key was made.`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="panel__body stack stack--tight"
      aria-label="Create an API key"
      onSubmit={(event) => void submit(event)}
    >
      <h3>Create an API key</h3>
      {problem === null ? null : <Notice tone="bad">{problem}</Notice>}
      <Field
        id="api-key-name"
        label="Name this key"
        placeholder="Deploy pipeline"
        autoFocus
        required
        disabled={busy}
        value={name}
        onChange={(event) => setName(event.target.value)}
      />
      <fieldset disabled={busy}>
        <legend className="field__label">What it can do</legend>
        {PRESETS.map(([preset, label, hint]) => (
          <label key={preset} className="checkline">
            <input
              type="radio"
              name="api-key-scope"
              value={preset}
              checked={scope === preset}
              onChange={() => setScope(preset)}
            />
            <span>
              {label} <small className="field__hint">{hint}</small>
            </span>
          </label>
        ))}
      </fieldset>
      <div className="actions">
        <button type="submit" className="btn btn--primary" disabled={busy || name.trim() === ""}>
          {busy ? "Creating…" : "Create key"}
        </button>
        <button type="button" className="btn" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/** Focus this heading once, when it is first drawn. */
function useFocusOnMount(ref: RefObject<HTMLHeadingElement | null>) {
  useEffect(() => {
    ref.current?.focus();
  }, [ref]);
}

function ShownOnce({ shown, onDismiss }: { shown: Shown; onDismiss: () => void }) {
  const heading = useRef<HTMLHeadingElement>(null);
  useFocusOnMount(heading);
  // Leaving the page puts the key away as the button does (#929).
  useDropOnPageHide(onDismiss);
  return (
    <div className="panel__body stack stack--tight" data-testid="api-key-shown-once">
      <h3 ref={heading} tabIndex={-1}>
        Copy your new key
      </h3>
      <Notice tone="good">
        {shown.rotated
          ? `“${shown.name}” was rotated: the old key no longer works.`
          : `“${shown.name}” was created.`}
      </Notice>
      <p>Copy this key now and keep it somewhere safe: it is shown only once.</p>
      <p className="break">
        <code>{shown.key}</code>
      </p>
      <div className="actions">
        <button type="button" className="btn btn--primary" onClick={onDismiss}>
          I have copied this key
        </button>
      </div>
    </div>
  );
}

/** Revoke or rotate one key, asked before it is sent. */
type Pending = { act: "revoke" | "rotate"; row: ApiKeyRow };

function Confirm({
  pending,
  actions,
  onDone,
  onRevokedOnly,
  onCancel,
}: {
  pending: Pending;
  actions: ApiKeyActions;
  onDone: (rotated: MintedApiKey | null) => Promise<void>;
  /** A rotate that revoked the old key and made no new one, with why. */
  onRevokedOnly: (why: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const { act, row } = pending;
  const heading = useRef<HTMLHeadingElement>(null);
  useFocusOnMount(heading);

  /** Whether the old key is revoked now, as the list reads it; null when it cannot be read. */
  const revokedNow = async (): Promise<boolean | null> => {
    try {
      const rows = await actions.list();
      return rows.find((one) => one.api_key_id === row.api_key_id)?.status === "revoked";
    } catch {
      return null;
    }
  };

  const confirm = async () => {
    setProblem(null);
    setBusy(true);
    try {
      if (act === "revoke") {
        await actions.revoke(row.api_key_id);
        await onDone(null);
      } else {
        await onDone(await actions.rotate(row.api_key_id));
      }
    } catch (failure) {
      const why = messageOf(failure);
      const gone = act === "rotate" ? await revokedNow() : false;
      if (gone === true) {
        await onRevokedOnly(why);
        return;
      }
      setProblem(
        gone === null
          ? `${why} The old key may already be revoked: read the list again before retrying.`
          : `${why} Nothing was changed.`,
      );
      setBusy(false);
    }
  };

  return (
    <div className="panel__body stack stack--tight" data-testid="api-key-confirm">
      <h3 ref={heading} tabIndex={-1}>
        {act === "revoke" ? "Revoke" : "Rotate"} “{row.name}”
      </h3>
      <p>
        {act === "revoke"
          ? "The key stops working now, and anything using it is refused."
          : "The key stops working now, and a new one with the same access replaces it. Anything using the old key is refused until it has the new one."}
      </p>
      {problem === null ? null : <Notice tone="bad">{problem}</Notice>}
      <div className="actions">
        <button
          type="button"
          className="btn btn--danger"
          disabled={busy}
          onClick={() => void confirm()}
        >
          {act === "revoke" ? "Revoke key" : "Rotate key"}
        </button>
        <button type="button" className="btn" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function KeyTable({
  rows,
  readOnly,
  onAct,
}: {
  rows: ApiKeyRow[];
  readOnly: boolean;
  onAct: (pending: Pending) => void;
}) {
  return (
    <div className="table-wrap" tabIndex={0}>
      <table className="table">
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Key</th>
            <th scope="col">Access</th>
            <th scope="col">Created</th>
            <th scope="col">Last used</th>
            <th scope="col">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.api_key_id}>
              <td>
                <strong>{row.name}</strong>
              </td>
              <td>
                <code>{row.key_prefix}…</code>
              </td>
              <td>{ACCESS[row.scope_preset] ?? row.scope_preset}</td>
              <td>{day(row.created_at)}</td>
              <td>{row.last_used_at === null ? "never" : day(row.last_used_at)}</td>
              <td>
                {row.status !== "active" ? (
                  <Chip tone="bad">revoked</Chip>
                ) : readOnly ? null : (
                  <div className="actions">
                    <button
                      type="button"
                      className="btn btn--small"
                      onClick={() => onAct({ act: "rotate", row })}
                    >
                      Rotate
                    </button>
                    <button
                      type="button"
                      className="btn btn--small btn--danger"
                      onClick={() => onAct({ act: "revoke", row })}
                    >
                      Revoke
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function YourApiKeys({
  actions,
  canManage,
}: {
  actions: ApiKeyActions;
  /** Whether this session's role holds `api_keys:manage`. */
  canManage: boolean;
}) {
  const readOnly = useReadOnly() || !canManage;
  const [rows, setRows] = useState<ApiKeyRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);
  const [shown, setShown] = useState<Shown | null>(null);
  const [revoked, setRevoked] = useState<string | null>(null);
  const [revokedOnly, setRevokedOnly] = useState<{ name: string; why: string } | null>(null);
  // After an act that removes the pressed button, focus returns to the panel's heading.
  const heading = useRef<HTMLHeadingElement>(null);
  const [refocus, setRefocus] = useState(0);
  useEffect(() => {
    if (refocus > 0) heading.current?.focus();
  }, [refocus]);
  const backToHeading = () => setRefocus((n) => n + 1);

  // The console rebuilds its actions every render; the list is read on mount and
  // after each change, not on every render.
  const live = useRef(actions);
  live.current = actions;
  const refresh = useCallback(async () => {
    try {
      setRows(await live.current.list());
      setError(null);
    } catch (failure) {
      setError(messageOf(failure));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const active = rows?.filter((row) => row.status === "active").length ?? 0;
  const busyElsewhere = creating || pending !== null || shown !== null;
  return (
    <section className="panel" id="api-keys" aria-label="API keys">
      <div className="panel__head">
        <h2 ref={heading} tabIndex={-1}>
          API keys
        </h2>
        {rows === null ? null : <Chip>{active} active</Chip>}
      </div>
      <div className="panel__body stack stack--tight">
        <p className="muted">
          Keys for this workspace, for scripts and pipelines that call the API. Each one acts
          with the access it was made with.
        </p>
        {error === null ? null : <Notice tone="bad">{error}</Notice>}
        {revoked === null ? null : <Notice tone="good">“{revoked}” was revoked.</Notice>}
        {revokedOnly === null ? null : (
          <Notice tone="bad">
            “{revokedOnly.name}” was revoked, and no new key was made: {revokedOnly.why} Create a
            new key to replace it.
          </Notice>
        )}
        {rows === null && error === null ? <p className="muted">Reading your keys…</p> : null}
        {rows !== null && rows.length === 0 ? <p className="muted">No API keys yet.</p> : null}
      </div>
      {rows !== null && rows.length > 0 ? (
        <KeyTable
          rows={rows}
          readOnly={readOnly || busyElsewhere}
          onAct={(next) => {
            setRevoked(null);
            setRevokedOnly(null);
            setPending(next);
          }}
        />
      ) : null}
      {shown === null ? null : (
        <ShownOnce
          shown={shown}
          onDismiss={() => {
            setShown(null);
            backToHeading();
          }}
        />
      )}
      {pending === null ? null : (
        <Confirm
          key={`${pending.act}-${pending.row.api_key_id}`}
          pending={pending}
          actions={actions}
          onCancel={() => {
            setPending(null);
            backToHeading();
          }}
          onDone={async (rotated) => {
            const name = pending.row.name;
            setPending(null);
            if (rotated === null) {
              setRevoked(name);
              backToHeading();
            } else setShown({ key: rotated.api_key, name, rotated: true });
            await refresh();
          }}
          onRevokedOnly={async (why) => {
            const name = pending.row.name;
            setPending(null);
            setRevokedOnly({ name, why });
            backToHeading();
            await refresh();
          }}
        />
      )}
      {readOnly || pending !== null || shown !== null ? null : creating ? (
        <Create
          actions={actions}
          onCancel={() => {
            setCreating(false);
            backToHeading();
          }}
          onMade={async (made) => {
            setCreating(false);
            setShown({ key: made.api_key, name: made.name, rotated: false });
            await refresh();
          }}
        />
      ) : (
        <div className="panel__body actions">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => {
              setRevoked(null);
              setRevokedOnly(null);
              setCreating(true);
            }}
          >
            Create an API key
          </button>
        </div>
      )}
    </section>
  );
}
